

























import { Vue, Component, Prop } from 'vue-property-decorator';
import Plyr from 'plyr/dist/plyr.polyfilled.js';
import 'plyr/dist/plyr.css';
import { EVideoPlayerType } from '@/video/player/types/enums/video-player-type';
import { VideoLinkHelper } from '@/video/link/helpers/video-link.helper';
import { UrlHelper } from '@/helpers/url.helper';
import { throttle } from '@lib-shared/helpers';

@Component({
  data: () => ({
    EVideoPlayerType,
  }),
})
export default class VideoPlayer extends Vue {
  @Prop() file: File;
  @Prop() url: string;
  @Prop({ default: true }) controls: boolean;
  @Prop({ default: true }) clickToPlay: boolean;
  @Prop() autoplay: boolean;
  @Prop() muted: boolean;
  @Prop() playOnHover: boolean;
  @Prop() ratio: string;
  @Prop() thumbUrl: string;

  player = null;

  get videoStream(): string {
    if (!this.file) {
      return null;
    }
    const videoBlob: Blob = this.file as Blob;
    return window.URL.createObjectURL(videoBlob);
  }

  get youtubeId(): string {
    return VideoLinkHelper.getYoutubeURLValidation(this.url).id;
  }

  get vimeoId(): string {
    return VideoLinkHelper.getVimeoURLValidation(this.url).id;
  }

  handleTimeupdate() {
    this.$emit('timeupdate', Math.floor(this.player.currentTime * 1000));
  }

  handlePlaying() {
    this.$emit('playing');
  }

  handlePause() {
    this.$emit('pause');
  }

  handleEnded() {
    this.$emit('ended');
  }

  pause() {
    this.player.pause();
  }

  play() {
    this.player.play();
  }

  handleMouseEnter() {
    const isHTMLPlayer: boolean =
      this.videoPlayerType === EVideoPlayerType.Url ||
      this.videoPlayerType === EVideoPlayerType.File;

    if (this.playOnHover && isHTMLPlayer) {
      this.play();
    }
  }

  handleMouseLeave() {
    const isHTMLPlayer: boolean =
      this.videoPlayerType === EVideoPlayerType.Url ||
      this.videoPlayerType === EVideoPlayerType.File;

    if (this.playOnHover && isHTMLPlayer) {
      this.pause();
    }
  }

  throttledHandleTimeUpdate = throttle(this.handleTimeupdate, 1000);

  initialize() {
    this.player = new Plyr(this.$refs.player, {
      settings: [],
      storage: { enabled: false },
      autoplay: this.autoplay,
      muted: this.muted,
      clickToPlay: this.clickToPlay,
      ratio: this.ratio,
      controls: this.controls
        ? [
            'play-large',
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'airplay',
            'fullscreen',
          ]
        : [],
      resetOnEnd: true,
    });
    this.player.on('timeupdate', this.throttledHandleTimeUpdate);
    this.player.on('playing', this.handlePlaying);
    this.player.on('pause', this.handlePause);
    this.player.on('ended', this.handleEnded);
  }

  mounted() {
    this.initialize();
  }

  get videoPlayerType(): EVideoPlayerType {
    if (this.file) {
      return EVideoPlayerType.File;
    }
    if (VideoLinkHelper.getYoutubeURLValidation(this.url).valid) {
      return EVideoPlayerType.Youtube;
    }
    if (VideoLinkHelper.getVimeoURLValidation(this.url).valid) {
      return EVideoPlayerType.Vimeo;
    }
    if (UrlHelper.isValidUrl(this.url) || this.isBlob(this.url)) {
      return EVideoPlayerType.Url;
    }
    return EVideoPlayerType.Unknown;
  }

  isBlob(url: string): boolean {
    return url.indexOf('blob') > -1;
  }
}
